import { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { API, apiFetch } from "../lib/api";

const normalizeNumberStatus = (numbers = []) => {
  const current = numbers[0];
  if (!current) return { status: "disconnected", sessionInfo: null };
  return {
    status: current.status || "disconnected",
    sessionInfo: current.connectedAt || current.jid
      ? { connectedAt: current.connectedAt || null, phone: (current.jid || "").replace(/^\+/, "") || null }
      : null,
  };
};

const useSocket = (token, onNewMessage, onMaintenance, isOwner = true) => {
  const [socket,      setSocket]      = useState(null);
  const [status,      setStatus]      = useState("disconnected");
  const [qr,          setQr]          = useState("");
  const [sessionInfo, setSessionInfo] = useState(null);
  const pollRef = useRef(null);
  const memberNumberIdsRef = useRef(new Set());

  useEffect(() => {
    if (!token) return;

    // Send the JWT itself. Previously this decoded `userId` out of the token
    // client-side and passed it as a query param, and the server joined that
    // room without verifying anything — so any client could stream any
    // tenant's messages and QR codes just by editing the value. The room is
    // now derived server-side from the verified token; nothing the client
    // sends selects a room.
    const sock = io(API, {
      auth:                { token },
      transports:          ["websocket", "polling"],
      reconnection:        true,
      reconnectionAttempts:25,
      reconnectionDelay:   1500,
      reconnectionDelayMax:15000,
      timeout:             20000,
    });

    setSocket(sock);

    // An expired or revoked token now fails the handshake rather than silently
    // connecting to nothing. Surface it so the UI can prompt a re-login
    // instead of appearing merely "disconnected".
    sock.on("connect_error", (err) => {
      if (String(err?.message || "").startsWith("unauthorized")) {
        console.warn("[socket] auth rejected:", err.message);
        sock.disconnect();
      }
    });

    sock.on("status",      (s)    => {
      if (!isOwner) return;
      setStatus(prev => (prev === "qr" && s === "connecting" ? prev : s));
      if (s !== "qr" && s !== "connecting") setQr("");
      if (s === "disconnected" || s === "logged_out") setSessionInfo(null);
    });
    sock.on("qr",          (d)    => {
      if (!isOwner) return;
      if (d) { setQr(d); setStatus("qr"); } else setQr("");
    });
    sock.on("logged_out",  ()     => {
      if (!isOwner) return;
      setStatus("disconnected");
      setQr("");
      setSessionInfo(null);
    });
    sock.on("session_info",(info) => {
      if (!isOwner) return;
      if (info?.connectedAt) setSessionInfo(info);
    });
    sock.on("number_status", ({ numberId, status: numberStatus }) => {
      if (isOwner) return;
      if (memberNumberIdsRef.current.size && !memberNumberIdsRef.current.has(numberId)) return;
      setStatus(numberStatus || "disconnected");
      if (numberStatus !== "qr") setQr("");
      if (numberStatus === "disconnected" || numberStatus === "logged_out") setSessionInfo(null);
    });
    sock.on("number_qr", ({ numberId, qr: numberQr }) => {
      if (isOwner) return;
      if (memberNumberIdsRef.current.size && !memberNumberIdsRef.current.has(numberId)) return;
      if (numberQr) { setQr(numberQr); setStatus("qr"); }
      else setQr("");
    });
    sock.on("number_info", ({ numberId, jid, connectedAt }) => {
      if (isOwner) return;
      if (memberNumberIdsRef.current.size && !memberNumberIdsRef.current.has(numberId)) return;
      setSessionInfo({ connectedAt: connectedAt || null, phone: (jid || "").replace(/^\+/, "") || null });
    });
    sock.on("maintenance", (data) => { if (onMaintenance) onMaintenance(data); });

    // Polling fallback
    pollRef.current = setInterval(async () => {
      try {
        if (isOwner) {
          const d = await apiFetch("/api/status", {}, token);
          if (d?.status) {
            setStatus(prev => {
              if ((prev === "qr" || prev === "connecting") && d.status === "disconnected") return prev;
              return d.status;
            });
          }
          if (d?.connectedAt && d?.status === "connected") {
            setSessionInfo({ connectedAt: d.connectedAt, phone: d.phone });
          }
        } else {
          const d = await apiFetch("/api/whatsapp/numbers", {}, token);
          memberNumberIdsRef.current = new Set((d?.numbers || []).map(n => n.id));
          const next = normalizeNumberStatus(d?.numbers || []);
          setStatus(next.status);
          setSessionInfo(next.sessionInfo);
        }
      } catch {}
    }, 30_000);

    if (!isOwner) {
      apiFetch("/api/whatsapp/numbers", {}, token)
        .then(d => {
          memberNumberIdsRef.current = new Set((d?.numbers || []).map(n => n.id));
          const next = normalizeNumberStatus(d?.numbers || []);
          setStatus(next.status);
          setSessionInfo(next.sessionInfo);
        })
        .catch(() => {});
    }

    return () => { sock.disconnect(); clearInterval(pollRef.current); };
  }, [token, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket || !onNewMessage) return;
    socket.on("new_message", onNewMessage);
    return () => socket.off("new_message", onNewMessage);
  }, [socket, onNewMessage]);

  return { socket, status, setStatus, qr, setQr, sessionInfo };
};

export default useSocket;
